import { IconUser } from '@src/assets/icon-user';
import Link from 'next/link';
import { signOut } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UserMenuItem {
  id: string;
  label: string;
  link?: string;
  action?: () => void;
}

const getMenuItems = (callbackUrl: string): UserMenuItem[] => [
  { id: 'watchlist', label: 'Watchlist', link: '/watchlist' },
  { id: 'settings', label: 'Einstellungen', link: '/settings' },
  {
    id: 'auth/logout',
    label: 'Abmelden',
    action: () => signOut({ callbackUrl }),
  },
];

export const UserMenu = ({ callbackUrl }: { callbackUrl: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const items = getMenuItems(callbackUrl);

  const open = useCallback(() => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const delayedClose = useCallback(() => {
    closeTimer.current = setTimeout(close, 150);
  }, [close]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, close]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div ref={wrapperRef} className="relative h-full" onMouseEnter={open} onMouseLeave={delayedClose}>
      <button
        type="button"
        className="flex h-full items-center justify-center rounded-full border-0.5 border-transparent px-2 font-bold tracking-widest transition-colors duration-200 hover:border-tvmedia hover:text-tvmedia md:px-4 md:text-sm lg:text-base"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Benutzermenü"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <IconUser classProps={{ root: 'size-3.5 md:size-4.5 text-tvmedia' }} />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-2 min-w-32 rounded-2xl border-[0.5px] border-gray-650/65 bg-gray-800/80 backdrop-blur-md"
        >
          {items.map((item, idx) => (
            <div
              key={item.id}
              role="menuitem"
              tabIndex={idx}
              className="mx-5 border-gray-650/65 border-b py-3 text-center last:border-none"
            >
              {item.link ? (
                <Link
                  href={item.link}
                  prefetch={false}
                  className="font-bold text-[15px] text-white decoration-2 hover:underline hover:decoration-tvmedia hover:underline-offset-4"
                  onClick={close}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  type="button"
                  className="font-bold text-[15px] text-white decoration-2 hover:underline hover:decoration-tvmedia hover:underline-offset-4"
                  onClick={() => {
                    item.action?.();
                    close();
                  }}
                >
                  {item.label}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
